import React, { useState, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import IconButton from 'apollo-react/components/IconButton';
import Close from 'apollo-react-icons/Close';
import Check from 'apollo-react-icons/Check';
import {
  validateField,
  validateRepeaterChildren,
} from '../../../../utils/formCrafter/formatValidationError';
import { getFieldComponent, FIELD_CATEGORIES } from '../inputs/fieldRegistry';
import useFieldHandlers from '../../../../hooks/ui/formFields/useFieldHandlers';
import { getValidatedFileConfig } from '../../../../utils/formCrafter/fileTypeUtils';
import { DEFAULT_FILE_CONFIG } from '../../../../constants/formCrafter.constant';
import styles from './FieldEditor.module.css';

/**
 * FieldEditor - Reusable inline field editor component
 * Uses common form components from fieldRegistry for consistency
 */
const FieldEditor = ({
  field,
  value,
  onChange,
  onConfirm,
  onCancel,
  existingFiles = [],
  onServerFileDelete,
  formId,
  subformId,
  onStagedFileUpload,
  onStagedFileDelete,
  eagerUploadEnabled = false,
  initialStagedFiles = [],
}) => {
  const { t } = useTranslation();
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const fieldType = field?.type;

  const fileConfig = useMemo(() => {
    const defaults =
      DEFAULT_FILE_CONFIG[fieldType] || DEFAULT_FILE_CONFIG.file || {};
    return getValidatedFileConfig(
      {
        type: fieldType,
        accept: field?.accept,
        maxItems: field?.maxItems,
        maxSize: field?.maxSize,
      },
      defaults
    );
  }, [fieldType, field?.accept, field?.maxItems, field?.maxSize]);

  // Use the field's configured maxItems from fileConfig
  const effectiveFileConfig = useMemo(() => fileConfig, [fileConfig]);

  const { handlers, normalizedValues } = useFieldHandlers(
    onChange,
    value,
    effectiveFileConfig
  );

  const getValueForValidation = useCallback(() => {
    if (FIELD_CATEGORIES.FILE.includes(fieldType)) {
      // Check if file input ref has the hasFiles method
      if (fileInputRef.current?.hasFiles) {
        // Use the component's internal check which accounts for pending deletions
        return fileInputRef.current.hasFiles() ? ['__has_files__'] : [];
      }
      // Fallback to original logic
      const draftFiles = Array.isArray(value) ? value : [];
      const fileFieldElement = document.querySelector(
        `[data-file-field-id="${field.id}"]`
      );
      const hasStagedFilesInComponent =
        fileFieldElement?.getStagedFileIds?.()?.length > 0;
      if (draftFiles.length > 0) return draftFiles;
      if (hasStagedFilesInComponent) return ['__has_staged_files__'];
      if (Array.isArray(existingFiles) && existingFiles.length > 0) {
        return ['__has_existing_files__'];
      }
      return [];
    }
    return value;
  }, [existingFiles, fieldType, value, field?.id]);

  const handleConfirm = useCallback(async () => {
    const validationError = validateField(getValueForValidation(), field, t);
    if (validationError) {
      setError(validationError);
      return;
    }

    // For repeater fields, also validate required/type-specific rules on child fields
    if (
      FIELD_CATEGORIES.REPEATER.includes(fieldType) &&
      Array.isArray(value) &&
      value.length > 0
    ) {
      const childErrors = validateRepeaterChildren(value, field, t);
      if (Object.keys(childErrors).length > 0) {
        setError(Object.values(childErrors)[0]);
        return;
      }
    }

    setError(null);

    // Confirm pending file deletions BEFORE calling parent's onConfirm.
    // Must await so deletions complete and states update before editor closes.
    if (fileInputRef.current?.confirmPendingDeletions) {
      await fileInputRef.current.confirmPendingDeletions();
    }

    onConfirm();
  }, [field, fieldType, t, onConfirm, getValueForValidation, value]);

  const handleCancel = useCallback(async () => {
    // Cancel pending file deletions before calling parent's onCancel
    if (fileInputRef.current?.cancelPendingDeletions) {
      fileInputRef.current.cancelPendingDeletions();
    }
    // Cancel pending uploads (delete files uploaded during this edit session)
    if (fileInputRef.current?.cancelPendingUploads) {
      await fileInputRef.current.cancelPendingUploads();
    }
    onCancel();
  }, [onCancel]);

  const handleBlur = useCallback(() => {
    const validationError = validateField(getValueForValidation(), field, t);
    setError(validationError);
  }, [field, t, getValueForValidation]);

  const normalizedOptions = useMemo(() => {
    const options = field.options || field.values || [];
    if (!options || options.length === 0) return [];
    return options.map((opt) => ({
      label: opt.label || opt.value || String(opt),
      value: opt.value ?? opt.key ?? opt.label ?? String(opt),
    }));
  }, [field.options, field.values]);

  const renderInput = () => {
    const FieldComponent = getFieldComponent(fieldType);
    const showError = !!error;
    const helperText = error || '';

    const baseProps = {
      id: field.id,
      placeholder: field.placeholder,
      helperText,
      showError,
      disabled: false,
      required: field.required,
    };

    if (FIELD_CATEGORIES.TEXT.includes(fieldType)) {
      return (
        <FieldComponent
          {...baseProps}
          type={fieldType}
          value={value}
          onChange={handlers.handleTextChange}
          onBlur={handleBlur}
          min={field.min}
          max={field.max}
          step={field.step}
          minRows={field.minRows || 2}
        />
      );
    }

    if (FIELD_CATEGORIES.PHONE.includes(fieldType)) {
      return (
        <FieldComponent
          {...baseProps}
          value={value}
          onChange={handlers.handlePhoneChange}
          onBlur={handleBlur}
          countryCode={field.countryCode}
        />
      );
    }

    if (FIELD_CATEGORIES.SELECT.includes(fieldType)) {
      return (
        <FieldComponent
          {...baseProps}
          value={value}
          onChange={handlers.handleSelectChange}
          onBlur={handleBlur}
          options={normalizedOptions}
          multiple={fieldType === 'select' && Boolean(field.multiselect)}
        />
      );
    }

    if (FIELD_CATEGORIES.DATE.includes(fieldType)) {
      const isDateOnly = fieldType === 'date';
      if (isDateOnly) {
        return (
          <FieldComponent
            {...baseProps}
            type={fieldType}
            value={value}
            onChange={handlers.handleDateChange}
            onBlur={handleBlur}
          />
        );
      }
      return (
        <FieldComponent
          {...baseProps}
          type={fieldType}
          value={value}
          onChange={onChange}
          onBlur={handleBlur}
        />
      );
    }

    if (FIELD_CATEGORIES.RADIO.includes(fieldType)) {
      return (
        <FieldComponent
          {...baseProps}
          value={value}
          onChange={handlers.handleRadioChange}
          options={normalizedOptions}
        />
      );
    }

    if (FIELD_CATEGORIES.CHECKBOX.includes(fieldType)) {
      const hasOptions = normalizedOptions.length > 0;
      return (
        <FieldComponent
          {...baseProps}
          value={
            hasOptions
              ? normalizedValues.checkboxValue
              : normalizedValues.booleanValue
          }
          onGroupChange={handlers.handleCheckboxGroupChange}
          onSingleChange={handlers.handleSingleCheckboxChange}
          options={normalizedOptions}
        />
      );
    }

    if (FIELD_CATEGORIES.SWITCH.includes(fieldType)) {
      return (
        <FieldComponent
          {...baseProps}
          value={normalizedValues.booleanValue}
          onChange={handlers.handleSwitchChange}
        />
      );
    }

    if (FIELD_CATEGORIES.FILE.includes(fieldType)) {
      return (
        <FieldComponent
          {...baseProps}
          ref={fileInputRef}
          value={normalizedValues.fileValue}
          onUpload={handlers.handleFileUpload}
          onDelete={handlers.handleFileDelete}
          onServerFileDelete={onServerFileDelete}
          fileConfig={effectiveFileConfig}
          existingFiles={existingFiles}
          formId={formId}
          subformId={subformId}
          onStagedFileUpload={onStagedFileUpload}
          onStagedFileDelete={onStagedFileDelete}
          eagerUploadEnabled={eagerUploadEnabled}
          initialStagedFiles={initialStagedFiles}
          enablePendingDelete={true}
        />
      );
    }

    if (FIELD_CATEGORIES.SLIDER.includes(fieldType)) {
      return (
        <FieldComponent
          {...baseProps}
          value={value}
          onChange={onChange}
          min={field.min}
          max={field.max}
          step={field.step}
        />
      );
    }

    if (FIELD_CATEGORIES.RATING.includes(fieldType)) {
      return (
        <FieldComponent
          {...baseProps}
          value={value}
          onChange={onChange}
          max={field.max}
        />
      );
    }

    if (FIELD_CATEGORIES.REPEATER.includes(fieldType)) {
      return (
        <FieldComponent
          {...baseProps}
          ref={fileInputRef}
          label=""
          value={value}
          onChange={onChange}
          onBlur={handleBlur}
          fields={field.fields}
          // Pass file-related props for nested file fields within repeater items
          existingFiles={existingFiles}
          onServerFileDelete={onServerFileDelete}
          formId={formId}
          subformId={subformId}
          onStagedFileUpload={onStagedFileUpload}
          onStagedFileDelete={onStagedFileDelete}
          eagerUploadEnabled={eagerUploadEnabled}
          initialStagedFiles={initialStagedFiles}
          // Enable pending delete for file fields within repeater items
          enablePendingDelete={true}
        />
      );
    }

    return (
      <FieldComponent
        {...baseProps}
        type="text"
        value={value}
        onChange={handlers.handleTextChange}
        onBlur={handleBlur}
      />
    );
  };

  return (
    <div className={styles.container}>
      <div className={styles.inputWrapper}>{renderInput()}</div>
      <div className={styles.actions}>
        <IconButton
          size="small"
          className={styles.iconButton}
          onClick={handleConfirm}
          title={t('confirm')}
        >
          <Check className={styles.icon} />
        </IconButton>
        <IconButton
          size="small"
          className={styles.iconButton}
          onClick={handleCancel}
          title={t('cancel')}
        >
          <Close className={styles.icon} />
        </IconButton>
      </div>
    </div>
  );
};

export default FieldEditor;
